import { MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp, PageHero, Eyebrow } from "@/components/Reveal";

const VALUES = [
  { n: "01", title: "Esclusività", text: "Selezioniamo solo profili con reale potenziale per dedicare a ciascuno una regia manageriale su misura. Non siamo una fabbrica di account generici." },
  { n: "02", title: "Riservatezza", text: "Accordi di riservatezza (NDA) e protocolli permanenti di protezione dell'identità: la tutela della privacy è la base imprescindibile di ogni nostra partnership." },
  { n: "03", title: "Performance", text: "Ogni scelta è guidata dai dati. L'incremento dei ricavi e l'elevazione dello status del brand sono le uniche metriche che contano." },
  { n: "04", title: "Trasparenza Societaria", text: "Rapporti chiari, tutela contrattuale e gestione professionale certificata. La fiducia si consolida sui risultati e sulla correttezza operativa." },
  { n: "05", title: "Scalabilità", text: "Costruiamo asset digitali duraturi e indipendenti nel tempo, evitando picchi temporanei di visibilità per garantire una stabilità economica costante." },
];

const STRUCTURE = [
  { title: "Management", text: "Direzione strategica e relazione diretta con ogni creator." },
  { title: "Sales & Support", text: "Team operativo dedicato a vendite e gestione delle conversazioni, 24/7." },
  { title: "Growth & Media", text: "Traffico, contenuti e analisi dati per alimentare la crescita." },
  { title: "Legal & Privacy", text: "Tutela contrattuale, protezione dell'identità e dei contenuti." },
];

export default function ChiSiamo() {
  const { openContact } = useSite();

  return (
    <main data-testid="chi-siamo-page">
      <PageHero
        testid="chi-siamo-hero"
        eyebrow="Chi siamo"
        lines={[
          "Esclusività. Performance.",
          <span key="l2" className="gold-text gold-text-animated italic">Riservatezza.</span>,
        ]}
        sub="IEM — Iconic Elite Management S.R.L.S. è una società italiana di digital management creata per trasformare la presenza dei creator in brand d'élite scalabili e monetizzabili."
      />

      <section className="py-14 lg:py-20" data-testid="vision-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2">
          <FadeUp>
            <Eyebrow>La visione</Eyebrow>
            <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-[#F5F3EE]">
              Standard aziendali d'élite, <span className="italic text-gold">applicati all'immagine digitale.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-sm sm:text-base leading-relaxed text-[#969696]">
              IEM nasce da un principio chiaro: i creator non sono semplici profili social, ma figure centrali di
              veri e propri business digitali. Per questo operiamo come una società di capitali regolamentata — con
              contratti blindati, processi operativi definiti e un team di vendita h24 dedicato.
            </p>
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#969696]">
              La creator economy, con ecosistemi di monetizzazione ad alta conversione come OnlyFans e i principali
              canali social, richiede un approccio rigoroso, riservatezza assoluta e visione strategica. Questo è il
              nostro standard operativo.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>I valori fondanti</Eyebrow>
          </FadeUp>
          <div className="mt-12">
            {VALUES.map((v, i) => (
              <FadeUp key={v.n} delay={i * 0.05}>
                <div
                  data-testid={`value-${v.n}`}
                  className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-t border-[#1A1A1A] py-8 last:border-b sm:grid-cols-[80px_280px_1fr] lg:gap-12"
                >
                  <span className="font-serif text-xl text-gold/60 transition-colors group-hover:text-gold">{v.n}</span>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#F5F3EE]">{v.title}</h3>
                  <p className="col-span-2 sm:col-span-1 text-sm leading-relaxed text-[#969696]">{v.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="structure-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>La struttura</Eyebrow>
            <p className="mt-6 max-w-3xl text-base sm:text-lg leading-relaxed text-[#969696]">
              Un'organizzazione aziendale al servizio del tuo brand. Dalla direzione strategica e la regia dei
              contenuti fino alla gestione vendite 24/7, mettiamo a disposizione un'infrastruttura completa per
              consentire a ogni creator di eccellere senza dispersioni di tempo.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STRUCTURE.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.08} className="border-t border-gold/30 pt-6">
                <div data-testid={`structure-${s.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <h3 className="font-serif text-xl text-[#F5F3EE]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#969696]">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25} className="mt-14">
            <button
              data-testid="chi-siamo-cta"
              onClick={() => openContact("creator")}
              className="inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
            >
              <MessageCircle size={16} /> Candidati come Creator
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
